import { Archive, Eye, FileEdit, Heart, House, Plus, Search, Star, X } from "lucide-react";
import { useMemo, useState } from "react";
import type { TripStatus, TripSummary } from "../types";

type Filter = "ALL" | "FAVORITES" | TripStatus;

interface Props {
  trips: TripSummary[];
  favorites: string[];
  activeId: string;
  canCreate: boolean;
  canFavorite: boolean;
  onOpen: (id: string) => void;
  onCreate: () => void;
  onToggleFavorite: (id: string) => void;
  onClose: () => void;
  fullPage?: boolean;
  onGoHome?: () => void;
}

const statusCopy: Record<TripStatus, string> = {
  DRAFT: "草稿",
  IN_REVIEW: "审阅中",
  PUBLISHED: "已发布",
  ARCHIVED: "已归档"
};

export function RouteLibrary({ trips, favorites, activeId, canCreate, canFavorite, onOpen, onCreate, onToggleFavorite, onClose, fullPage = false, onGoHome }: Props) {
  const [filter, setFilter] = useState<Filter>("ALL");
  const [query, setQuery] = useState("");
  const visible = useMemo(() => trips.filter((trip) => {
    if (filter === "FAVORITES" && !favorites.includes(trip.id)) return false;
    if (filter !== "ALL" && filter !== "FAVORITES" && trip.status !== filter) return false;
    return `${trip.name} ${trip.short ?? ""}`.toLowerCase().includes(query.trim().toLowerCase());
  }), [favorites, filter, query, trips]);

  return (
    <div className={fullPage ? "route-library-page" : "dialog-backdrop"} role="presentation" onMouseDown={(event) => !fullPage && event.target === event.currentTarget && onClose()}>
      <section className={`route-library${fullPage ? " is-page" : ""}`} role={fullPage ? "region" : "dialog"} aria-modal={fullPage ? undefined : "true"} aria-labelledby="route-library-title">
        <header>
          <div>{fullPage && onGoHome && <button className="library-home" onClick={onGoHome}><House size={17} />首页</button>}<span className="dialog-mark">旅行档案</span><h2 id="route-library-title">路线库</h2><p>{trips.length} 条路线 · {favorites.length} 条收藏</p></div>
          <div>{canCreate && <button className="primary-button" onClick={onCreate}><Plus size={16} />新建路线</button>}{!fullPage && <button className="icon-button" onClick={onClose} aria-label="关闭路线库"><X size={18} /></button>}</div>
        </header>
        <div className="library-toolbar">
          <label><Search size={16} /><input name="library-query" aria-label="搜索路线" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索路线名称或简介" /></label>
          <div role="tablist" aria-label="筛选路线">
            {(["ALL", "FAVORITES", "DRAFT", "IN_REVIEW", "PUBLISHED", "ARCHIVED"] as Filter[]).map((value) => <button key={value} role="tab" aria-selected={filter === value} className={filter === value ? "is-active" : ""} onClick={() => setFilter(value)}>{value === "ALL" ? "全部" : value === "FAVORITES" ? "收藏" : statusCopy[value]}</button>)}
          </div>
        </div>
        <div className="library-grid">
          {visible.map((trip) => <article key={trip.id} className={trip.id === activeId ? "is-active" : ""}>
            <span className={`library-status is-${trip.status.toLowerCase()}`}>{trip.status === "PUBLISHED" ? <Eye size={14} /> : trip.status === "ARCHIVED" ? <Archive size={14} /> : <FileEdit size={14} />}{statusCopy[trip.status]}</span>
            <h3>{trip.name}</h3>
            <p>{trip.short || "尚未填写路线简介"}</p>
            <small>{trip.dayCount} 天 · {trip.placeCount} 个地点 · {trip.visibility === "PRIVATE" ? "私有" : trip.visibility === "LINK" ? "持链接可见" : "公开"}</small>
            <footer><button onClick={() => { onOpen(trip.id); onClose(); }}>{trip.id === activeId ? "返回当前路线" : "打开路线"}</button>{canFavorite && <button className={favorites.includes(trip.id) ? "is-favorite" : ""} onClick={() => onToggleFavorite(trip.id)} aria-label={favorites.includes(trip.id) ? `取消收藏 ${trip.name}` : `收藏 ${trip.name}`}><Star size={16} fill={favorites.includes(trip.id) ? "currentColor" : "none"} /></button>}</footer>
          </article>)}
          {!visible.length && <div className="library-empty"><Heart size={28} /><strong>这里暂时没有路线</strong><span>调整筛选条件，或创建一条新的旅行路线。</span></div>}
        </div>
      </section>
    </div>
  );
}
